// Cliente HTTP del reporte de helpdesk (GET /api/v1/helpdesk/reports/summary).
//
// Replica el patrón de services/aiChatService.js en vez de importarlo: ese
// archivo lo está editando otro flujo en paralelo y este módulo debe poder
// vivir solo. Mismo BFF, mismos headers, mismos errores tipados con `.status`.

// Typed error shared with the other helpdesk clients: it keeps the HTTP status
// (isEndpointMissing) and the backend validation lists in err.messages.
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final (p. ej.
// https://api.beartrackapp.com/api/v1/). Se recorta para armar rutas completas
// /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * true cuando el error viene de una ruta aún no desplegada (404/405). La vista
 * lo usa para degradar a PendingBackendNotice en vez de mostrar error rojo:
 * mientras el microservicio de reportes no esté en producción, el BFF responde
 * 404 a esta ruta.
 * @param {unknown} err
 * @returns {boolean}
 */
export const isEndpointMissing = (err) => [404, 405].includes(err?.status);

/**
 * Resumen agregado del helpdesk para un rango de fechas.
 * Contrato del backend (defaults últimos 30 días, máximo 92):
 * { ok, from, to, kpis, por_dia, por_canal, por_agente, por_carpeta, por_hora }
 * @param {object} params
 * @param {string} [params.from] - ISO (inicio de día local)
 * @param {string} [params.to]   - ISO (fin de día local)
 * @returns {Promise<object>}
 */
export async function getReportsSummary({ from, to } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/reports/summary?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Query string del listado de casos. Solo agrega los filtros definidos: el
 * backend aplica sus defaults (rango 30 días, limit 100) a lo que no viaje.
 * Nada sensible en la URL — la auth va siempre en headers.
 * @param {object} params
 * @returns {URLSearchParams}
 */
function buildCasesQuery({ from, to, agent_id, channel, folder_id } = {}) {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (agent_id) qs.set("agent_id", agent_id);
  if (channel) qs.set("channel", channel);
  if (folder_id) qs.set("folder_id", folder_id);
  return qs;
}

/**
 * "YYYY-MM-DD" LOCAL a partir de un ISO, para el nombre de archivo de
 * respaldo. Sin toISOString(): en Chile (UTC-3/-4) correría el día.
 * @param {string} iso
 * @returns {string}
 */
function isoToLocalYMD(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Listado plano de casos (drill-down del reporte). Misma población que las
 * métricas del summary: casos sellados + caso vigente dentro del rango.
 * Contrato: { ok, from, to, total, limit, offset, rows } con fila
 * { conversation_id, case_number, contact_name, contact_phone, channel,
 *   folder_id, folder_name, opened_at, resolved_at, resolved_by, assigned_to,
 *   status, duration_min|null }.
 * @param {object} params
 * @param {string} [params.from] - ISO
 * @param {string} [params.to] - ISO
 * @param {string} [params.agent_id] - filtra por assigned_to O resolved_by
 * @param {string} [params.channel]
 * @param {string} [params.folder_id]
 * @param {number} [params.limit]
 * @param {number} [params.offset]
 * @returns {Promise<object>}
 */
export async function getReportCases({ from, to, agent_id, channel, folder_id, limit, offset } = {}) {
  const qs = buildCasesQuery({ from, to, agent_id, channel, folder_id });
  // limit/offset admiten 0: se comparan contra null/undefined, no truthiness.
  if (limit !== undefined && limit !== null) qs.set("limit", String(limit));
  if (offset !== undefined && offset !== null) qs.set("offset", String(offset));

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/reports/cases?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Consumo y costo estimado de los agentes de IA en una ventana de días.
 * Contrato del backend (GET /helpdesk/ai-usage?days=7|30):
 * { ok, days, agents: [{ agent_id, name, status, provider, model, replies,
 *   conversations, escalations, actions, tokens_in, tokens_out,
 *   cost_usd_est: number|null, budget: { max_daily_tokens, max_daily_actions,
 *   today_tokens, today_actions } }], totals: {...}, series: [{ day, tokens_in,
 *   tokens_out, actions }] }.
 *
 * `cost_usd_est` es null cuando el modelo del agente no tiene tarifa cargada:
 * es ausencia de precio, no un costo de cero (ver formatUsd).
 * @param {number} [days=7] - ventana en días (el backend acepta 7 y 30)
 * @returns {Promise<object>}
 */
export async function getAiUsage(days = 7) {
  const qs = new URLSearchParams();
  // 7 por defecto: el mismo default del backend, para que un llamador sin
  // argumentos no dependa de que el servidor adivine la ventana.
  if (days !== undefined && days !== null) qs.set("days", String(days));

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-usage?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Descarga el CSV de casos con los mismos filtros del listado y dispara el
 * "guardar como" del navegador. El nombre sale del Content-Disposition del
 * backend (casos_<from>_<to>.csv) con fallback local si el header no llega
 * (p. ej. si el BFF no lo propaga).
 * @param {object} params - mismos filtros que getReportCases (sin paginación)
 * @returns {Promise<void>}
 */
export async function downloadCasesCsv({ from, to, agent_id, channel, folder_id } = {}) {
  const qs = buildCasesQuery({ from, to, agent_id, channel, folder_id });
  qs.set("format", "csv");

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/reports/cases?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^";]+)/);
  const filename =
    (match && match[1]) || `casos_${isoToLocalYMD(from)}_${isoToLocalYMD(to)}.csv`;

  // Ancla efímera: es la única forma cross-browser de nombrar la descarga.
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
